import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  finalize,
  forkJoin,
  skip,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { INVENTORY_ITEM_TYPE_SERVICE } from 'src/app/core/enums/inventory-item-type.enum';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderOverview } from 'src/app/core/models/sales/company-sales-order-overview.interface';
import { CompanyShipmentDetails } from 'src/app/core/models/sales/company-shipment-details.interface';
import { CreateShipmentStandaloneFormGroup } from 'src/app/core/models/sales/create-shipment-standalone-form-group.interface';
import { CreateShipmentStandaloneFormModel } from 'src/app/core/models/sales/create-shipment-standalone-form.model';
import { CreateShipmentStandaloneLineFormGroup } from 'src/app/core/models/sales/create-shipment-standalone-line-form-group.interface';
import { CreateShipmentStandaloneRequest } from 'src/app/core/models/sales/create-shipment-standalone-request.interface';
import { InventoryItemService } from 'src/app/core/services/inventory-item.service';
import { SalesOrderService } from 'src/app/core/services/sales-order.service';
import { ShipmentService } from 'src/app/core/services/shipment.service';
import {
  buildInventoryItemOption,
  inventoryItemOptionCompare,
  inventoryItemOptionFilter,
  inventoryItemOptionFormatter,
} from 'src/app/core/utils/inventory-item-search.util';
import { SalesOrderInventoryItemOption } from '../../sales-orders/sales-order-overview/sales-order-overview.models';

const INVENTORY_ITEM_LIST_PAGE_SIZE = 200;

interface SalesOrderOption {
  id: string;
  number: string;
  title: string;
  subtitle: string;
}

@Component({
  selector: 'app-create-shipment-modal',
  standalone: true,
  imports: [CommonModule, CoreModule, ReactiveFormsModule, TranslateModule],
  providers: [CreateShipmentStandaloneFormModel],
  templateUrl: './create-shipment-modal.component.html',
  styleUrl: './create-shipment-modal.component.scss',
})
export class CreateShipmentModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) public companyId = '';
  @Input() public isOpen = false;

  @Output() public readonly cancelled = new EventEmitter<void>();
  @Output() public readonly created = new EventEmitter<CompanyShipmentDetails>();

  public readonly today = CreateShipmentModalComponent.formatToday();

  public readonly unitsOfMeasure$ = new BehaviorSubject<UnitOfMeasureLookup[]>([]);
  public readonly inventoryItemOptions$ = new BehaviorSubject<SalesOrderInventoryItemOption[]>([]);
  public readonly salesOrderOptions$ = new BehaviorSubject<SalesOrderOption[]>([]);
  public readonly canPrefillFromOrder$ = new BehaviorSubject<boolean>(false);

  public readonly inventoryItemFilterFn = inventoryItemOptionFilter;
  public readonly inventoryItemFormatter = inventoryItemOptionFormatter;
  public readonly inventoryItemCompareFn = inventoryItemOptionCompare;

  public readonly salesOrderFilterFn = (option: SalesOrderOption, term: string): boolean => {
    const normalized = term.trim().toLowerCase();
    if (normalized.length === 0) {
      return true;
    }
    return (
      option.title.toLowerCase().includes(normalized) ||
      option.subtitle.toLowerCase().includes(normalized)
    );
  };
  public readonly salesOrderFormatter = (option: SalesOrderOption | null | undefined): string =>
    option?.title ?? '';
  public readonly salesOrderCompareFn = (a: SalesOrderOption, b: SalesOrderOption): boolean =>
    a.id === b.id;

  public isSubmitting = false;
  public errorMessageKey = '';

  private readonly unsubscribe$ = new Subject<void>();
  private readonly lineSubscriptions = new Subject<void>();
  private salesOrderDetailsCache = new Map<string, CompanySalesOrderDetails>();
  private currentSalesOrderDetails: CompanySalesOrderDetails | null = null;

  public readonly formModel = inject(CreateShipmentStandaloneFormModel);
  private readonly shipmentService = inject(ShipmentService);
  private readonly salesOrderService = inject(SalesOrderService);
  private readonly inventoryItemService = inject(InventoryItemService);

  public get form(): FormGroup<CreateShipmentStandaloneFormGroup> {
    return this.formModel.form;
  }

  public get lineForms(): FormArray<FormGroup<CreateShipmentStandaloneLineFormGroup>> {
    return this.formModel.lines;
  }

  public ngOnInit(): void {
    this.form.controls.shipmentDate.setValue(this.today);

    this.form.controls.salesOrderId.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((orderId) => this.handleSalesOrderSelection(orderId)),
      )
      .subscribe();

    // Inventory-item options load asynchronously after the modal opens. If a line's
    // `inventoryItemId` was already populated (e.g. by `prefillFromOrder`) before the
    // options arrived, the per-line subscription evaluated `isServiceItemId` against an
    // empty options buffer and the line stayed flagged as Product (keeping the warehouse
    // picker visible and the required-validator attached). Re-evaluate every existing
    // line whenever the options buffer emits to recover the correct Service classification.
    // `skip(1)` drops the BehaviorSubject's initial empty `[]` — no lines exist and no
    // Service items are buffered yet, so reacting to it would be wasted work.
    // Real emissions come from `loadLookups` → `forkJoin` → `inventoryItemOptions$.next(...)` below.
    this.inventoryItemOptions$
      .pipe(
        skip(1),
        takeUntil(this.unsubscribe$),
        tap(() => this.reapplyServiceRulesToExistingLines()),
      )
      .subscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.resetForm();
      this.loadLookups();
    }
  }

  public ngOnDestroy(): void {
    this.lineSubscriptions.next();
    this.lineSubscriptions.complete();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    if (this.isSubmitting) {
      return;
    }
    this.cancelled.emit();
  }

  public addLine(): void {
    const defaultUnitId = this.unitsOfMeasure$.value[0]?.id ?? null;
    const group = this.formModel.addLine({
      inventoryItemId: null,
      warehouseId: null,
      quantity: 0,
      unitOfMeasureId: defaultUnitId,
      salesOrderLineId: null,
    });
    this.attachLineSubscription(group);
  }

  public removeLine(index: number): void {
    this.formModel.removeLineAt(index);
  }

  public prefillFromOrder(): void {
    const order = this.currentSalesOrderDetails;
    if (!order) {
      return;
    }
    this.formModel.clearLines();
    order.lines.forEach((line) => {
      const group = this.formModel.addLine({
        inventoryItemId: line.inventoryItemId ?? null,
        warehouseId: null,
        quantity: line.quantity,
        unitOfMeasureId: line.unitOfMeasureId ?? this.unitsOfMeasure$.value[0]?.id ?? null,
        salesOrderLineId: line.id,
      });
      this.attachLineSubscription(group);
    });
  }

  /**
   * Subscribes to `inventoryItemId.valueChanges` for a single line FormGroup so the
   * warehouse validators / value can be toggled when the operator switches between
   * Product/Hybrid and Service items. The subscription is dropped on form reset
   * (`lineSubscriptions` next) and on component teardown (`unsubscribe$`).
   */
  private attachLineSubscription(
    group: FormGroup<CreateShipmentStandaloneLineFormGroup>,
  ): void {
    group.controls.inventoryItemId.valueChanges
      .pipe(
        startWith(group.controls.inventoryItemId.value),
        takeUntil(this.lineSubscriptions),
        takeUntil(this.unsubscribe$),
        tap((itemId) => {
          const isServiceItem = this.isServiceItemId(itemId);
          CreateShipmentStandaloneFormModel.applyServiceWarehouseRule(group, isServiceItem);
        }),
      )
      .subscribe();
  }

  private isServiceItemId(itemId: string | null | undefined): boolean {
    if (!itemId) {
      return false;
    }
    return (
      this.inventoryItemOptions$.value.find((option) => option.id === itemId)?.itemType === INVENTORY_ITEM_TYPE_SERVICE
    );
  }

  /**
   * Walks every existing FormArray line and re-applies the Service warehouse rule based on
   * the current `inventoryItemOptions$` buffer. Used when the options buffer resolves AFTER
   * a line's `inventoryItemId` was already populated (race window between `prefillFromOrder`
   * and `loadLookups`). Keeps the per-line `attachLineSubscription` `valueChanges` handler
   * unchanged — this method only catches lines whose item id was set before lookups arrived.
   */
  private reapplyServiceRulesToExistingLines(): void {
    this.lineForms.controls.forEach((line) => {
      const isServiceItem = this.isServiceItemId(line.controls.inventoryItemId.value);
      CreateShipmentStandaloneFormModel.applyServiceWarehouseRule(line, isServiceItem);
    });
  }

  public submitForm(): void {
    if (!this.companyId || this.isSubmitting) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.lineForms.length === 0) {
      this.errorMessageKey = 'COMPONENTS.SHIPMENTS.MESSAGES.NO_LINES';
      return;
    }

    const request = this.buildRequest();
    if (!request) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessageKey = '';

    this.shipmentService
      .createShipment(this.companyId, request)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((shipment) => this.created.emit(shipment)),
        catchError((error: HttpErrorResponse) => {
          this.errorMessageKey = this.mapErrorKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  private buildRequest(): CreateShipmentStandaloneRequest | null {
    const raw = this.form.getRawValue();
    const customerId = raw.customerId?.trim();
    const shipmentDate = raw.shipmentDate?.trim();
    if (!customerId || !shipmentDate) {
      return null;
    }
    return {
      customerId,
      salesOrderId: raw.salesOrderId?.trim() || null,
      shipmentDate: CreateShipmentModalComponent.toIsoDateTime(shipmentDate),
      lines: raw.lines.map((line) => {
        const trimmedWarehouse = line.warehouseId?.trim() ?? '';
        return {
          inventoryItemId: line.inventoryItemId ?? '',
          // Service lines MUST send `null` (backend rule). Product/Hybrid lines keep
          // sending the trimmed warehouse identifier as before. The `isServiceLine` flag
          // is maintained exclusively by `applyServiceWarehouseRule`, so reading it from
          // the raw form value is the single source of truth at submit time.
          warehouseId: line.isServiceLine ? null : trimmedWarehouse,
          quantity: Number(line.quantity ?? 0),
          unitOfMeasureId: Number(line.unitOfMeasureId ?? 0),
          salesOrderLineId: line.salesOrderLineId?.trim() || null,
        };
      }),
    };
  }

  private resetForm(): void {
    this.lineSubscriptions.next();
    this.formModel.reset();
    this.form.controls.shipmentDate.setValue(this.today);
    this.errorMessageKey = '';
    this.currentSalesOrderDetails = null;
    this.canPrefillFromOrder$.next(false);
  }

  private loadLookups(): void {
    if (!this.companyId) {
      return;
    }

    forkJoin({
      units: this.inventoryItemService.getUnitsOfMeasureLookup(this.companyId),
      inventoryItems: this.inventoryItemService.getCompanyInventoryItemsPaged(this.companyId, {
        filters: [],
        page: 1,
        pageSize: INVENTORY_ITEM_LIST_PAGE_SIZE,
      }),
      salesOrders: this.salesOrderService.getSalesOrders(this.companyId),
    })
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(({ units, inventoryItems, salesOrders }) => {
          this.unitsOfMeasure$.next(units);
          this.inventoryItemOptions$.next(inventoryItems.items.map(buildInventoryItemOption));
          this.salesOrderOptions$.next(salesOrders.map((order) => this.toSalesOrderOption(order)));
        }),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  private toSalesOrderOption(order: CompanySalesOrderOverview): SalesOrderOption {
    const number = order.number?.trim() || '—';
    return {
      id: order.id,
      number,
      title: number,
      subtitle: order.customerNumber || '',
    };
  }

  private handleSalesOrderSelection(orderId: string | null | undefined): Observable<unknown> {
    if (!orderId) {
      this.currentSalesOrderDetails = null;
      this.canPrefillFromOrder$.next(false);
      this.form.controls.customerId.setValue(null);
      return EMPTY;
    }

    const cached = this.salesOrderDetailsCache.get(orderId);
    if (cached) {
      this.applySalesOrderDetails(cached);
      return EMPTY;
    }

    return this.salesOrderService.getSalesOrder(this.companyId, orderId).pipe(
      tap((order) => {
        this.salesOrderDetailsCache.set(orderId, order);
        this.applySalesOrderDetails(order);
      }),
      catchError(() => {
        this.currentSalesOrderDetails = null;
        this.canPrefillFromOrder$.next(false);
        return EMPTY;
      }),
    );
  }

  private applySalesOrderDetails(order: CompanySalesOrderDetails): void {
    this.currentSalesOrderDetails = order;
    this.form.controls.customerId.setValue(order.customerId ?? null);
    this.canPrefillFromOrder$.next((order.lines?.length ?? 0) > 0);
  }

  // Map structured backend error codes to i18n keys. Keep in sync with the BE
  // ShipmentErrorCodes constants enumerated in §12 of the planning doc.
  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    SHIPMENT_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_INVALID_STATUS',
    SHIPMENT_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_TERMINAL',
    INSUFFICIENT_STOCK: 'COMPONENTS.SHIPMENTS.ERRORS.INSUFFICIENT_STOCK',
    ORDER_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_TERMINAL',
    ORDER_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_INVALID_STATUS',
  };

  private mapErrorKey(error: HttpErrorResponse): string {
    if (error.status === 400 || error.status === 409 || error.status === 422) {
      const code = (error.error as { code?: string } | null)?.code;
      if (code && this.errorCodeKeyMap[code]) {
        return this.errorCodeKeyMap[code];
      }
    }
    return 'COMPONENTS.SHIPMENTS.ERRORS.CREATE_FAILED';
  }

  private static formatToday(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private static toIsoDateTime(yyyyMmDd: string): string {
    return `${yyyyMmDd}T00:00:00Z`;
  }
}
